import {
  Alert,
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Select,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@marrafa-gioale/gymx-ui-web'
import { createFileRoute } from '@tanstack/react-router'
import { Archive, BookMarked, CalendarCheck, Copy, Pencil, Plus, UserPlus } from 'lucide-react'
import { useMemo, useState } from 'react'

import { errorMessage } from '@/api/errors'
import {
  useAssignmentMutations,
  useCurrentPermissions,
  useGymMemberSessions,
  useGymMembers,
  useMemberExerciseProgress,
  useTemplateMutations,
  useTrainingAdherence,
  useTrainingAssignment,
  useTrainingAssignments,
  useWorkoutTemplate,
  useWorkoutTemplates,
} from '@/api/queries'
import type {
  ExerciseProgressPoint,
  TrainingAssignment,
  TrainingAssignmentStatus,
  TrainingDayInput,
  TrainingGoal,
  Uuid,
  WorkoutTemplate,
} from '@/api/types'
import { PageHeader } from '@/components/layout/app-shell'
import { ProgramEditor } from '@/features/training/program-editor'
import { dayLabel, nameIndex, toDayInputs } from '@/features/training/program'
import { formatDate, formatNumber } from '@/lib/format'
import { notify } from '@/lib/toast'

/**
 * Le schede che il PT assegna — F5, TRN-01/02/03.
 *
 * ## Due elenchi, e non è una scelta di layout
 *
 * A sinistra i **modelli**, che sono catalogo dell'organizzazione; a destra le
 * **schede assegnate**, che sono di una persona. Sono due tabelle diverse per la
 * ragione che questa pagina deve rendere evidente: una scheda consegnata è una
 * **fotografia** del modello, e correggere il modello non la tocca. Un'unica
 * lista con un filtro suggerirebbe il contrario.
 *
 * ## «Cosa sta seguendo adesso»
 *
 * L'elenco delle assegnazioni parte da `activeOn` sul giorno di oggi, perché è la
 * domanda che il trainer si fa davanti a una persona. Lo stato non basta: una
 * scheda `active` che parte lunedì prossimo non è quella di oggi.
 *
 * ## Chi legge e chi scrive
 *
 * `card:read_any` mostra, `card:write_any` rende cliccabile. L'allenatore ha
 * entrambi; la reception nessuno dei due, e per lei questa pagina è vuota — con
 * una frase che lo dice, invece di un elenco che sembra rotto.
 */
export const Route = createFileRoute('/_app/schede/')({
  component: TrainingPage,
})

const GOAL_LABELS: Record<TrainingGoal, string> = {
  strength: 'Forza',
  hypertrophy: 'Ipertrofia',
  weight_loss: 'Dimagrimento',
  endurance: 'Resistenza',
  mobility: 'Mobilità',
  rehabilitation: 'Ripresa',
  general: 'Generale',
}

const STATUS_LABELS: Record<TrainingAssignmentStatus, string> = {
  active: 'In corso',
  completed: 'Conclusa',
  cancelled: 'Annullata',
}

const today = () => new Date().toISOString().slice(0, 10)

function TrainingPage() {
  const permissions = useCurrentPermissions()
  const canRead = permissions.has('card:read_any')
  const canWrite = permissions.has('card:write_any')

  if (!canRead) {
    return (
      <>
        <PageHeader title="Schede" description="Modelli e schede assegnate agli iscritti." />
        <Alert variant="neutral" title="Serve «card:read_any»" icon={false}>
          Il tuo ruolo non legge le schede altrui. È il permesso dell’allenatore, non del banco.
        </Alert>
      </>
    )
  }

  return (
    <>
      <PageHeader
        title="Schede"
        description="La libreria dei modelli e le schede assegnate. Una scheda consegnata è una copia: correggere il modello non la cambia."
      />
      <Tabs variant="pill" defaultValue="assignments">
        <TabsList>
          <TabsTrigger value="assignments">
            <CalendarCheck /> Assegnate
          </TabsTrigger>
          <TabsTrigger value="templates">
            <BookMarked /> Modelli
          </TabsTrigger>
        </TabsList>
        <TabsContent value="assignments">
          <AssignmentsPanel canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="templates">
          <TemplatesPanel canWrite={canWrite} />
        </TabsContent>
      </Tabs>
    </>
  )
}

// ── modelli ───────────────────────────────────────────────────────────────────

function TemplatesPanel({ canWrite }: { canWrite: boolean }) {
  const [archived, setArchived] = useState(false)
  const list = useWorkoutTemplates({ archived })
  const mutations = useTemplateMutations()
  const [editing, setEditing] = useState<{ id: Uuid | null } | null>(null)

  const rows = list.data?.data ?? []

  /**
   * Duplica e **apre subito la copia**: chi duplica lo fa per cambiarla — nessuno vuole una
   * seconda scheda identica —, e lasciarla nell'elenco costringerebbe a cercarla fra righe con
   * nomi quasi uguali, che è esattamente il momento in cui si apre quella sbagliata.
   */
  async function duplicate(template: WorkoutTemplate) {
    try {
      const copy = await mutations.duplicate.mutateAsync(template.id)
      notify({ message: `Creato «${copy.name}».`, variant: 'success' })
      setEditing({ id: copy.id })
    } catch (cause) {
      notify({ message: errorMessage(cause), variant: 'danger' })
    }
  }

  async function archive(template: WorkoutTemplate) {
    try {
      await mutations.archive.mutateAsync(template.id)
      notify({ message: `«${template.name}» archiviato.`, variant: 'success' })
    } catch (cause) {
      notify({ message: errorMessage(cause), variant: 'danger' })
    }
  }

  if (list.isError && !list.data) {
    return <ErrorState message={errorMessage(list.error)} onRetry={() => void list.refetch()} />
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={archived ? 'ghost' : 'outline'}
            onClick={() => setArchived(false)}
          >
            Vivi
          </Button>
          <Button
            size="sm"
            variant={archived ? 'outline' : 'ghost'}
            onClick={() => setArchived(true)}
          >
            Archiviati
          </Button>
        </div>
        {canWrite ? (
          <Button size="sm" variant="primary" onClick={() => setEditing({ id: null })}>
            <Plus />
            Nuovo modello
          </Button>
        ) : null}
      </div>

      {list.isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<BookMarked />}
          title={archived ? 'Nessun modello archiviato' : 'Nessun modello'}
          description="Un modello è la scheda che si riusa: si scrive una volta e si assegna a chi serve, e da lì in poi le due cose vivono separate."
        />
      ) : (
        <ul className="divide-border divide-y">
          {rows.map((template) => (
            <li key={template.id} className="flex flex-wrap items-center gap-3 py-3 first:pt-0">
              <div className="min-w-0 flex-1">
                <p className="font-medium">{template.name}</p>
                <p className="text-muted-foreground text-xs">
                  {template.dayCount === 0
                    ? 'Nessun giorno'
                    : `${String(template.dayCount)} giorni · ${String(template.exerciseCount)} esercizi`}
                  {template.createdByName ? ` · ${template.createdByName}` : ''}
                </p>
              </div>
              {template.goal ? <Badge>{GOAL_LABELS[template.goal]}</Badge> : null}
              <div className="flex gap-1.5">
                <Button size="sm" variant="ghost" onClick={() => setEditing({ id: template.id })}>
                  <Pencil />
                  {canWrite ? 'Modifica' : 'Apri'}
                </Button>
                {/* Duplica c'è **anche sugli archiviati**, e non per simmetria: ripartire da una
                    scheda dell'anno scorso è il caso per cui si guarda l'archivio, e la copia
                    nasce viva senza resuscitare l'originale. */}
                {canWrite ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={mutations.duplicate.isPending}
                    onClick={() => void duplicate(template)}
                  >
                    <Copy />
                    Duplica
                  </Button>
                ) : null}
                {template.archivedAt === null && canWrite ? (
                  <Button size="sm" variant="ghost" onClick={() => void archive(template)}>
                    <Archive />
                    Archivia
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing !== null ? (
        <TemplateDialog
          templateId={editing.id}
          canWrite={canWrite}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  )
}

/**
 * Creazione e modifica di un modello, intestazione e struttura insieme.
 *
 * Due richieste sul salvataggio di un modello esistente — `PATCH` per
 * l'intestazione, `PUT` per la struttura — e una sola alla creazione, che accetta
 * entrambe. È il contract, ed è la forma giusta: rinominare un modello non deve
 * rimandare al server le sue duecento righe.
 */
function TemplateDialog({
  templateId,
  canWrite,
  onClose,
}: {
  templateId: Uuid | null
  canWrite: boolean
  onClose: () => void
}) {
  const existing = useWorkoutTemplate(templateId)
  const mutations = useTemplateMutations()

  const [name, setName] = useState('')
  const [goal, setGoal] = useState<TrainingGoal | undefined>(undefined)
  const [days, setDays] = useState<TrainingDayInput[]>([])
  const [names] = useState(() => new Map<string, string>())
  const [loaded, setLoaded] = useState(templateId === null)
  const [error, setError] = useState<string | null>(null)

  if (!loaded && existing.data !== undefined) {
    const template = existing.data.data
    setName(template.name)
    setGoal(template.goal ?? undefined)
    setDays(toDayInputs(template.days))
    for (const [id, label] of nameIndex(template.days)) names.set(id, label)
    setLoaded(true)
  }

  const pending =
    mutations.create.isPending || mutations.update.isPending || mutations.replaceStructure.isPending

  async function submit() {
    if (name.trim() === '') {
      setError('Il nome del modello è obbligatorio.')
      return
    }
    setError(null)
    try {
      if (templateId === null) {
        await mutations.create.mutateAsync({
          name: name.trim(),
          goal: goal ?? null,
          days,
        })
        notify({ message: 'Modello creato.', variant: 'success' })
      } else {
        // L'intestazione per prima, e la **sua** `ETag` va nella seconda: ogni
        // scrittura fa avanzare la revisione, quindi riusare quella letta
        // costerebbe un `412`, e non mandarne nessuna sarebbe
        // ultima-scrittura-vince proprio dove due operatori sullo stesso
        // programma si sovrascriverebbero in silenzio.
        const updated = await mutations.update.mutateAsync({
          id: templateId,
          etag: existing.data?.etag ?? null,
          patch: { name: name.trim(), goal: goal ?? null },
        })
        await mutations.replaceStructure.mutateAsync({
          id: templateId,
          days,
          etag: updated.etag,
        })
        notify({ message: 'Modello aggiornato.', variant: 'success' })
      }
      onClose()
    } catch (cause) {
      setError(errorMessage(cause))
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{templateId === null ? 'Nuovo modello' : 'Modello'}</DialogTitle>
          <DialogDescription>
            Salvando si manda il programma intero: i giorni che non compaiono qui vengono tolti.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          {error !== null ? (
            <Alert variant="danger" title="Non salvato">
              {error}
            </Alert>
          ) : null}

          {!loaded ? (
            <Skeleton className="h-64 w-full" />
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Nome" required>
                  {(props) => (
                    <Input
                      {...props}
                      value={name}
                      maxLength={120}
                      disabled={!canWrite}
                      placeholder="Full body principianti"
                      onChange={(event) => setName(event.target.value)}
                    />
                  )}
                </Field>
                <Field label="Obiettivo">
                  {(props) => (
                    <Select
                      {...props}
                      value={goal}
                      onValueChange={(value) => setGoal(value as typeof goal)}
                      aria-label="Obiettivo"
                      disabled={!canWrite}
                      placeholder="Non dichiarato"
                      items={Object.entries(GOAL_LABELS).map(([value, label]) => ({
                        value: value as TrainingGoal,
                        label,
                      }))}
                    />
                  )}
                </Field>
              </div>

              <ProgramEditor days={days} onChange={setDays} names={names} disabled={!canWrite} />
            </>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {canWrite ? 'Annulla' : 'Chiudi'}
          </Button>
          {canWrite ? (
            <Button
              variant="primary"
              loading={pending}
              disabled={!loaded}
              onClick={() => void submit()}
            >
              Salva
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── schede assegnate ──────────────────────────────────────────────────────────

function AssignmentsPanel({ canWrite }: { canWrite: boolean }) {
  const [onlyToday, setOnlyToday] = useState(true)
  const list = useTrainingAssignments(onlyToday ? { activeOn: today() } : {})
  const [assigning, setAssigning] = useState(false)
  const [opened, setOpened] = useState<Uuid | null>(null)

  const rows = list.data?.data ?? []

  if (list.isError && !list.data) {
    return <ErrorState message={errorMessage(list.error)} onRetry={() => void list.refetch()} />
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={onlyToday ? 'outline' : 'ghost'}
            onClick={() => setOnlyToday(true)}
          >
            In corso oggi
          </Button>
          <Button
            size="sm"
            variant={onlyToday ? 'ghost' : 'outline'}
            onClick={() => setOnlyToday(false)}
          >
            Tutte
          </Button>
        </div>
        {canWrite ? (
          <Button size="sm" variant="primary" onClick={() => setAssigning(true)}>
            <UserPlus />
            Assegna una scheda
          </Button>
        ) : null}
      </div>

      {list.isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<CalendarCheck />}
          title={onlyToday ? 'Nessuna scheda in corso oggi' : 'Nessuna scheda assegnata'}
          description={
            onlyToday
              ? 'Una scheda che parte lunedì prossimo è già registrata e non compare qui: «in corso» guarda il periodo, non lo stato.'
              : 'Assegnare una scheda non richiede che l’iscritto abbia un account: il soggetto è l’anagrafica.'
          }
        />
      ) : (
        <ul className="divide-border divide-y">
          {rows.map((assignment) => (
            <li key={assignment.id} className="flex flex-wrap items-center gap-3 py-3 first:pt-0">
              <div className="min-w-0 flex-1">
                <p className="font-medium">{assignment.gymMemberName}</p>
                <p className="text-muted-foreground text-xs">
                  {assignment.name} · dal {assignment.startsOn}
                  {assignment.endsOn === null ? '' : ` al ${assignment.endsOn}`}
                  {` · ${String(assignment.dayCount)} giorni`}
                </p>
              </div>
              <StatusBadge status={assignment.status} />
              <Button size="sm" variant="ghost" onClick={() => setOpened(assignment.id)}>
                <Pencil />
                {canWrite ? 'Modifica' : 'Apri'}
              </Button>
            </li>
          ))}
        </ul>
      )}

      {assigning ? <AssignDialog onClose={() => setAssigning(false)} /> : null}
      {opened !== null ? (
        <AssignmentDialog
          assignmentId={opened}
          canWrite={canWrite}
          onClose={() => setOpened(null)}
        />
      ) : null}
    </div>
  )
}

function StatusBadge({ status }: { status: TrainingAssignmentStatus }) {
  const tone = status === 'active' ? 'success' : status === 'completed' ? 'neutral' : 'danger'
  return <Badge variant={tone}>{STATUS_LABELS[status]}</Badge>
}

/**
 * Assegnare una scheda: si sceglie la persona, e poi il modello **oppure** niente.
 *
 * I due non si mandano insieme — il server lo rifiuta con un `409` — e la pagina
 * non offre la combinazione: una scheda su misura si scrive dopo averla creata,
 * aprendola.
 */
function AssignDialog({ onClose }: { onClose: () => void }) {
  const templates = useWorkoutTemplates({ archived: false })
  const mutations = useAssignmentMutations()
  const [query, setQuery] = useState('')
  const members = useGymMembers({
    query: query.trim().length >= 2 ? query.trim() : undefined,
    limit: 8,
  })

  const [gymMemberId, setGymMemberId] = useState<Uuid | null>(null)
  const [gymMemberName, setGymMemberName] = useState('')
  const [templateId, setTemplateId] = useState<Uuid | undefined>(undefined)
  const [name, setName] = useState('')
  const [startsOn, setStartsOn] = useState(today())
  const [error, setError] = useState<string | null>(null)

  const chosenTemplate = (templates.data?.data ?? []).find((item) => item.id === templateId)

  async function submit() {
    if (gymMemberId === null) {
      setError('Scegli l’iscritto.')
      return
    }
    if (templateId === undefined && name.trim() === '') {
      setError('Senza un modello il nome della scheda è obbligatorio.')
      return
    }
    setError(null)
    try {
      await mutations.create.mutateAsync({
        gymMemberId,
        startsOn,
        ...(templateId !== undefined ? { templateId } : {}),
        ...(name.trim() !== '' ? { name: name.trim() } : {}),
      })
      notify({ message: 'Scheda assegnata.', variant: 'success' })
      onClose()
    } catch (cause) {
      setError(errorMessage(cause))
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assegna una scheda</DialogTitle>
          <DialogDescription>
            La copia dal modello è una fotografia: da qui in poi le due cose vivono separate.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          {error !== null ? (
            <Alert variant="danger" title="Non assegnata">
              {error}
            </Alert>
          ) : null}

          <Field label="Iscritto" required hint="Cerca per cognome, nome o numero di tessera.">
            {(props) => (
              <Input
                {...props}
                value={gymMemberId === null ? query : gymMemberName}
                placeholder="Rossi"
                onChange={(event) => {
                  setGymMemberId(null)
                  setQuery(event.target.value)
                }}
              />
            )}
          </Field>

          {gymMemberId === null && query.trim().length >= 2 ? (
            <ul className="border-border divide-border max-h-40 divide-y overflow-y-auto rounded-md border">
              {(members.data?.data ?? []).map((member) => (
                <li key={member.id}>
                  <button
                    type="button"
                    className="hover:bg-muted w-full px-2 py-1.5 text-left text-sm"
                    onClick={() => {
                      setGymMemberId(member.id)
                      setGymMemberName(`${member.lastName} ${member.firstName}`)
                    }}
                  >
                    {member.lastName} {member.firstName}
                  </button>
                </li>
              ))}
              {(members.data?.data ?? []).length === 0 ? (
                <li className="text-muted-foreground px-2 py-1.5 text-sm">Nessun iscritto.</li>
              ) : null}
            </ul>
          ) : null}

          <Field label="Modello" hint="Vuoto: la scheda nasce vuota e si scrive aprendola.">
            {(props) => (
              <Select
                {...props}
                value={templateId}
                onValueChange={setTemplateId}
                aria-label="Modello"
                placeholder="Nessuno — su misura"
                items={(templates.data?.data ?? []).map((template) => ({
                  value: template.id,
                  label: (
                    <span className="inline-flex items-center gap-2">
                      {template.name}
                      <span className="text-muted-foreground text-xs">{`${String(template.dayCount)} gg`}</span>
                    </span>
                  ),
                }))}
              />
            )}
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Nome della scheda"
              hint={
                chosenTemplate === undefined
                  ? 'Obbligatorio senza modello.'
                  : `Vuoto: «${chosenTemplate.name}», come il modello.`
              }
            >
              {(props) => (
                <Input
                  {...props}
                  value={name}
                  maxLength={120}
                  onChange={(event) => setName(event.target.value)}
                />
              )}
            </Field>
            <Field label="Comincia il" required hint="La fine si mette quando arriva.">
              {(props) => (
                <Input
                  {...props}
                  type="date"
                  value={startsOn}
                  onChange={(event) => setStartsOn(event.target.value)}
                />
              )}
            </Field>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button
            variant="primary"
            loading={mutations.create.isPending}
            onClick={() => void submit()}
          >
            Assegna
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** La scheda di una persona: intestazione, stato e programma. */
function AssignmentDialog({
  assignmentId,
  canWrite,
  onClose,
}: {
  assignmentId: Uuid
  canWrite: boolean
  onClose: () => void
}) {
  const existing = useTrainingAssignment(assignmentId)
  const mutations = useAssignmentMutations()

  const [days, setDays] = useState<TrainingDayInput[]>([])
  const [names] = useState(() => new Map<string, string>())
  const [status, setStatus] = useState<TrainingAssignmentStatus>('active')
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!loaded && existing.data !== undefined) {
    const assignment: TrainingAssignment = existing.data.data
    setDays(toDayInputs(assignment.days))
    setStatus(assignment.status)
    for (const [id, label] of nameIndex(assignment.days)) names.set(id, label)
    setLoaded(true)
  }

  const assignment = existing.data?.data
  const cancelled = status === 'cancelled'
  const pending = mutations.update.isPending || mutations.replaceStructure.isPending

  async function submit() {
    setError(null)
    try {
      const updated = await mutations.update.mutateAsync({
        id: assignmentId,
        etag: existing.data?.etag ?? null,
        patch: { status },
      })
      // Una scheda annullata non si riscrive: il server la rifiuta, e mandare la
      // struttura solo per incassare un `409` mostrerebbe un errore a chi ha solo
      // cambiato stato. L'`ETag` è quello che la `PATCH` ha appena prodotto.
      if (status !== 'cancelled') {
        await mutations.replaceStructure.mutateAsync({
          id: assignmentId,
          days,
          etag: updated.etag,
        })
      }
      notify({ message: 'Scheda aggiornata.', variant: 'success' })
      onClose()
    } catch (cause) {
      setError(errorMessage(cause))
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{assignment?.gymMemberName ?? 'Scheda'}</DialogTitle>
          <DialogDescription>
            {assignment === undefined
              ? null
              : `${assignment.name}${assignment.templateName ? ` · da «${assignment.templateName}»` : ' · su misura'}`}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          {error !== null ? (
            <Alert variant="danger" title="Non salvata">
              {error}
            </Alert>
          ) : null}

          {!loaded ? (
            <Skeleton className="h-64 w-full" />
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Stato">
                  {(props) => (
                    <Select
                      {...props}
                      value={status}
                      onValueChange={(value) => setStatus(value as typeof status)}
                      aria-label="Stato"
                      disabled={!canWrite}
                      items={Object.entries(STATUS_LABELS).map(([value, label]) => ({
                        value: value as TrainingAssignmentStatus,
                        label,
                      }))}
                    />
                  )}
                </Field>
                <div className="text-muted-foreground self-end text-xs">
                  {assignment?.assignedByName ? `Assegnata da ${assignment.assignedByName}` : null}
                </div>
              </div>

              <AdherencePanel
                assignmentId={assignmentId}
                gymMemberId={assignment?.gymMemberId ?? null}
              />

              <LoadProgressPanel
                gymMemberId={assignment?.gymMemberId ?? null}
                days={days}
                names={names}
              />

              {cancelled ? (
                <Alert variant="warning" title="Annullata" icon={false}>
                  Il programma di una scheda annullata non si riscrive: correggere l’allenamento di
                  un periodo annullato è la traccia di un errore, non un aggiornamento.
                </Alert>
              ) : null}

              {days.length > 0 && !canWrite ? (
                <ul className="text-muted-foreground space-y-1 text-sm">
                  {days.map((day, index) => (
                    <li key={day.id ?? index}>
                      {dayLabel(day)} — {String(day.exercises.length)} esercizi
                    </li>
                  ))}
                </ul>
              ) : (
                <ProgramEditor
                  days={days}
                  onChange={setDays}
                  names={names}
                  disabled={!canWrite || cancelled}
                />
              )}
            </>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {canWrite ? 'Annulla' : 'Chiudi'}
          </Button>
          {canWrite ? (
            <Button
              variant="primary"
              loading={pending}
              disabled={!loaded}
              onClick={() => void submit()}
            >
              Salva
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Quanto la scheda viene seguita — TRN-12.
 *
 * È la domanda che chi assegna si fa dopo aver assegnato, e prima del diario non
 * aveva risposta. Il rapporto lo calcola il **server**: due schermate lo
 * calcolerebbero in due modi, e quello sbagliato si scoprirebbe in una riunione.
 *
 * Senza un previsto — una scheda ancora senza giorni — il rapporto non si mostra
 * affatto: uno zero direbbe che quella persona non si allena, mentre il dato che
 * manca è il programma.
 */
function AdherencePanel({
  assignmentId,
  gymMemberId,
}: {
  assignmentId: Uuid
  gymMemberId: Uuid | null
}) {
  const adherence = useTrainingAdherence(assignmentId)
  const sessions = useGymMemberSessions(gymMemberId, 5)

  if (adherence.isPending) return <Skeleton className="h-20 w-full" />
  const data = adherence.data
  if (data === undefined) return null

  const rows = sessions.data?.data ?? []
  const percent = data.rate === null ? null : Math.round(data.rate * 100)

  return (
    <div className="border-border space-y-3 rounded-lg border p-3">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <p className="text-sm font-medium">Aderenza</p>
        {percent === null ? (
          <p className="text-muted-foreground text-sm">
            Il programma non ha ancora giorni: senza un previsto non c’è un rapporto.
          </p>
        ) : (
          <p className="text-sm">
            <span className="text-lg font-semibold" data-numeric>
              {percent}%
            </span>
            <span className="text-muted-foreground ml-2">
              {data.completedSessions} allenamenti su {data.expectedSessions} previsti in{' '}
              {data.weeksElapsed} settiman{data.weeksElapsed === 1 ? 'a' : 'e'} (
              {data.expectedPerWeek} a settimana)
            </span>
          </p>
        )}
      </div>

      {rows.length === 0 ? (
        <p className="text-muted-foreground text-xs">
          Nessun allenamento registrato in questa palestra.
        </p>
      ) : (
        <ul className="text-muted-foreground space-y-1 text-xs">
          {rows.map((session) => (
            <li key={session.id}>
              {new Date(session.startedAt).toLocaleDateString('it-IT', {
                day: 'numeric',
                month: 'short',
              })}
              {session.dayLabel === null ? '' : ` · ${session.dayLabel}`}
              {` · ${String(session.setCount)} serie`}
              {session.totalVolumeKg === null
                ? ''
                : ` · ${String(Math.round(session.totalVolumeKg))} kg`}
              {/* Una in corso non conta nell'aderenza, e dirlo qui evita la
                  domanda «perché la percentuale non è salita». */}
              {session.open ? ' · in corso' : ''}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * «Migliora?» — TRN-15, la terza domanda del pannello sull'allenamento.
 *
 * ## Le altre due non la rispondono
 *
 * L'aderenza qui sopra dice «quanto segue il programma?» e il diario «si allena?». Una
 * persona può allenarsi ogni martedì per un anno **senza aggiungere un chilo**, e quel caso
 * è invisibile a entrambe: era il residuo dichiarato di TRN-15, e la conseguenza pratica era
 * che l'unico posto in cui l'andamento dei carichi si vedeva era l'app dell'iscritto — cioè
 * non il posto in cui sta chi scrive il programma.
 *
 * ## Gli esercizi da scegliere sono quelli del programma
 *
 * Non il catalogo intero: la domanda di chi ha assegnato una scheda è «migliora sugli
 * esercizi che gli ho dato», e un elenco di duemila voci la seppellirebbe. Quelli in più che
 * quella persona fa da sé si vedono nel suo diario, ed è il posto giusto.
 *
 * ## Un valore che manca non è uno zero, e qui si vede
 *
 * Un esercizio senza carico — un plank, una trazione — ha `bestWeightKg: null`. Disegnarlo a
 * zero direbbe «non è migliorato» di chi ha raddoppiato le ripetizioni: quando il carico non
 * c'è, il pannello mostra le **ripetizioni** e lo dichiara.
 *
 * ## Nessuna libreria di grafici
 *
 * Una polilinea SVG di venti righe, come le barre di CSS delle analisi
 * (`features/analytics/blocks.tsx`): quel file dichiara che una dipendenza avrà senso «il
 * giorno in cui servirà una serie temporale con lo zoom». Questa non ha lo zoom.
 */
function LoadProgressPanel({
  gymMemberId,
  days,
  names,
}: {
  gymMemberId: Uuid | null
  days: TrainingDayInput[]
  /**
   * I nomi per id, dalla stessa mappa che alimenta l'editor: le righe in scrittura
   * (`TrainingExerciseInput`) **non portano il nome** — lo denormalizza il server —, e senza
   * questa mappa il selettore mostrerebbe `seed_Barbell_Squat` invece di «Barbell Squat».
   */
  names: Map<string, string>
}) {
  // Gli esercizi del programma, una volta ciascuno: lo stesso esercizio in tre giorni è una
  // sola serie storica, perché la domanda è sull'esercizio e non sul giorno.
  const options = useMemo(() => {
    const seen = new Map<string, string>()
    for (const day of days)
      for (const exercise of day.exercises)
        if (!seen.has(exercise.exerciseId))
          seen.set(exercise.exerciseId, names.get(exercise.exerciseId) ?? exercise.exerciseId)
    return [...seen.entries()].map(([value, label]) => ({ value, label }))
  }, [days, names])

  const [exerciseId, setExerciseId] = useState<string>('')
  const progress = useMemberExerciseProgress(gymMemberId, exerciseId === '' ? null : exerciseId)

  if (gymMemberId === null || options.length === 0) return null

  const points = progress.data?.points ?? []
  const weights = points.map((point) => point.bestWeightKg).filter((value) => value !== null)
  const withoutLoad = points.length > 0 && weights.length === 0

  return (
    <div className="border-border space-y-3 rounded-lg border p-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <p className="text-sm font-medium">Andamento dei carichi</p>
          <p className="text-muted-foreground text-xs">
            «Si allena?» lo dice l’aderenza qui sopra. Questa è un’altra domanda: sta migliorando?
          </p>
        </div>
        <Field label="Esercizio" className="w-56">
          {(props) => (
            <Select
              {...props}
              value={exerciseId}
              onValueChange={setExerciseId}
              aria-label="Esercizio"
              items={[{ value: '', label: 'Scegli un esercizio…' }, ...options]}
            />
          )}
        </Field>
      </div>

      {exerciseId === '' ? (
        <p className="text-muted-foreground text-xs">
          Gli esercizi sono quelli di questo programma. Un esercizio per volta: un grafico con
          quaranta linee non lo legge nessuno.
        </p>
      ) : progress.isPending ? (
        <Skeleton className="h-24 w-full" />
      ) : progress.isError ? (
        <Alert variant="danger">{errorMessage(progress.error)}</Alert>
      ) : points.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          Nessun allenamento chiuso con questo esercizio, in questa palestra. Una sessione ancora
          aperta non è un punto: si muoverebbe mentre la si guarda.
        </p>
      ) : (
        <ProgressChart points={points} withoutLoad={withoutLoad} />
      )}
    </div>
  )
}

/**
 * La serie storica, in SVG.
 *
 * Disegna il **carico migliore** per sessione, o le ripetizioni migliori quando il carico non
 * esiste: sono due grandezze diverse e la legenda lo dice, invece di lasciare indovinare cosa
 * sia la linea.
 *
 * `viewBox` con `preserveAspectRatio="none"`: la larghezza è quella del contenitore e
 * l'altezza è fissa. Un grafico che cambia proporzioni con la finestra è accettabile qui
 * perché ciò che si legge è la **direzione**, non l'angolo.
 */
function ProgressChart({
  points,
  withoutLoad,
}: {
  points: ExerciseProgressPoint[]
  withoutLoad: boolean
}) {
  const values = points.map((point) =>
    withoutLoad ? (point.bestReps ?? 0) : (point.bestWeightKg ?? 0),
  )
  const max = Math.max(...values)
  const min = Math.min(...values)
  // Una serie piatta non deve diventare una linea a metà altezza per un caso: se tutti i
  // valori sono uguali il grafico la disegna in alto, che è dove sta un massimo.
  const span = max - min === 0 ? 1 : max - min
  const width = 100
  const height = 32
  const step = points.length === 1 ? 0 : width / (points.length - 1)
  const line = values
    .map((value, index) => {
      const x = points.length === 1 ? width / 2 : index * step
      const y = height - ((value - min) / span) * (height - 4) - 2
      return `${String(Math.round(x * 100) / 100)},${String(Math.round(y * 100) / 100)}`
    })
    .join(' ')

  const first = values[0] ?? 0
  const last = values[values.length - 1] ?? 0
  const unit = withoutLoad ? 'ripetizioni' : 'kg'

  return (
    <div className="space-y-2">
      <svg
        viewBox={`0 0 ${String(width)} ${String(height)}`}
        preserveAspectRatio="none"
        className="text-primary-content h-16 w-full"
        role="img"
        aria-label={`Andamento: da ${String(first)} a ${String(last)} ${unit}`}
      >
        {points.length === 1 ? (
          <circle cx={width / 2} cy={height / 2} r={1.5} fill="currentColor" />
        ) : (
          <polyline
            points={line}
            fill="none"
            stroke="currentColor"
            strokeWidth={1}
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        )}
      </svg>
      <p className="text-muted-foreground text-xs">
        {withoutLoad ? (
          <>
            Questo esercizio non porta un carico: la linea sono le <strong>ripetizioni</strong>
            migliori. Un grafico a zero direbbe che non è migliorato.
          </>
        ) : (
          <>
            Carico migliore per allenamento, in kg. Un punto è una sessione chiusa, non un giorno.
          </>
        )}
      </p>
      <p className="text-sm">
        <span data-numeric>{formatNumber(first)}</span> →{' '}
        <span className="font-semibold" data-numeric>
          {formatNumber(last)}
        </span>{' '}
        {unit}
        <span className="text-muted-foreground ml-2">
          · massimo {formatNumber(max)} · {points.length}{' '}
          {points.length === 1 ? 'allenamento' : 'allenamenti'} · dal {formatDate(points[0]?.date)}
        </span>
      </p>
    </div>
  )
}
